/**
 * Divot Works — size-selection analytics
 * --------------------------------------
 * Tracks every size-related choice a shopper makes and forwards it to
 * GA4, Meta and the GTM dataLayer. Loaded once, sitewide, from
 * layout/theme.liquid (same pattern as dw-carousel-analytics.js).
 *
 * Events
 *   size_guide_open         — the global size-guide modal opened
 *   size_guide_size_select  — a size tile "Select" CTA clicked in the modal
 *   variant_option_select   — a Size or Dexterity dropdown changed
 *                             (covers sections/main-product.liquid AND every
 *                             sections/featured-product.liquid instance,
 *                             because it hooks the shared variant-selects
 *                             component via a delegated listener)
 *
 * Destinations (independent flags below — mirror dw-carousel-analytics.js):
 *   • window.dataLayer.push(...)          → GTM (GTM-T4N2HWMR)
 *   • window.gtag('event', ...)           → GA4 G-QV078EVJTS (direct)
 *   • window.fbq(...)                     → Meta pixel (direct);
 *     variant_option_select goes out as the STANDARD event
 *     "CustomizeProduct" so it can drive optimisation/audiences.
 *   • Shopify.analytics.publish(...)      → Shopify customer-events stream
 *
 * If GTM tags are ever built for these events, set sendDirectToGa4 /
 * sendDirectToMeta to false to avoid double counting.
 *
 * A/B test: every event carries ab_theme_id / ab_theme_name from
 * window.Shopify.theme, so the Markets-based theme test (main vs
 * "PDP with Reviews") is a segmentable dimension in every destination.
 *
 * De-duping: assets/size-guide-modal.js applySize() intentionally re-fires
 * the change event up to 5 times (race-condition guard). A per-option
 * value+time window collapses those to one tracked event.
 */
(function () {
  'use strict';

  var CONFIG = {
    sendToDataLayer: true,
    sendDirectToGa4: true,
    sendDirectToMeta: true,
    publishToShopifyAnalytics: true,
    debug: false
  };

  var PLACEHOLDER_RE = /^select\s/i; // "Select Size", "Select Dexterity"
  var DEDUPE_WINDOW_MS = 1500;

  var lastValueByEl = (typeof WeakMap === 'function') ? new WeakMap() : null;
  var lastReported = {}; // option_name -> { value, ts }
  var lastClick = { el: null, ts: 0 };
  var sizeGuideClickTs = 0;
  var sgUrlParamAtLoad = false;
  var loadTs = Date.now();
  var modalWasOpen = false;

  /* ---------------- helpers ---------------- */

  function themeInfo() {
    var t = (window.Shopify && window.Shopify.theme) || {};
    return { id: t.id || null, name: t.name || null };
  }

  function pageType() {
    var main = document.querySelector('main[data-template]');
    if (main && main.dataset.template) return main.dataset.template;
    var m = (document.body.className || '').match(/template-([\w-]+)/);
    return m ? m[1] : 'unknown';
  }

  function baseParams() {
    var t = themeInfo();
    return {
      ab_theme_id: t.id,
      ab_theme_name: t.name,
      page_type: pageType()
    };
  }

  function merge(a, b) {
    var out = {}, k;
    for (k in a) if (Object.prototype.hasOwnProperty.call(a, k)) out[k] = a[k];
    for (k in b) if (Object.prototype.hasOwnProperty.call(b, k)) out[k] = b[k];
    return out;
  }

  function toPascal(name) {
    return name.split('_').map(function (p) {
      return p.charAt(0).toUpperCase() + p.slice(1);
    }).join('');
  }

  function send(eventName, params, metaOpts) {
    var payload = merge(baseParams(), params);
    if (CONFIG.debug && window.console) console.log('[dw-size-analytics]', eventName, payload);

    if (CONFIG.sendToDataLayer) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(merge({ event: eventName }, payload));
      } catch (e) { /* never break the storefront */ }
    }

    if (CONFIG.sendDirectToGa4 && typeof window.gtag === 'function') {
      try { window.gtag('event', eventName, payload); } catch (e) { /* noop */ }
    }

    if (CONFIG.sendDirectToMeta && typeof window.fbq === 'function') {
      try {
        if (metaOpts && metaOpts.standardEvent) {
          window.fbq('track', metaOpts.standardEvent, merge(payload, metaOpts.extra || {}));
        } else {
          window.fbq('trackCustom', toPascal(eventName), payload);
        }
      } catch (e) { /* noop */ }
    }

    if (CONFIG.publishToShopifyAnalytics) {
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish(eventName, payload);
        }
      } catch (e) { /* noop */ }
    }
  }

  /* ---------------- variant dropdown tracking ---------------- */

  function normalizeOptionName(select) {
    // name="options[Mat Size]" -> "mat_size"
    var raw = select.getAttribute('name') || '';
    var m = raw.match(/^options\[(.+)\]$/);
    var label = m ? m[1] : raw;
    return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'option';
  }

  function seedSelect(select) {
    if (lastValueByEl && !lastValueByEl.has(select)) lastValueByEl.set(select, select.value);
  }

  function seedAll() {
    var selects = document.querySelectorAll('variant-selects select');
    for (var i = 0; i < selects.length; i++) seedSelect(selects[i]);
  }

  function selectionMethod(optionName) {
    var now = Date.now();
    if (now - sizeGuideClickTs < 3000) return 'size_guide';
    if (sgUrlParamAtLoad && optionName.indexOf('size') !== -1 && now - loadTs < 10000) return 'size_guide_url';
    return 'user';
  }

  function onDocumentChange(e) {
    var select = e.target;
    if (!select || !select.matches || !select.matches('variant-selects select')) return;

    var optionName = normalizeOptionName(select);
    var value = select.value;
    var now = Date.now();

    // De-dupe programmatic re-fires (size-guide applySize fires up to 5x)
    var rep = lastReported[optionName];
    if (rep && rep.value === value && now - rep.ts < DEDUPE_WINDOW_MS) {
      rep.ts = now;
      return;
    }

    var prev = null;
    if (lastValueByEl && lastValueByEl.has(select)) prev = lastValueByEl.get(select);
    if (lastValueByEl) lastValueByEl.set(select, value);
    if (prev === value) return; // element-level de-dupe

    lastReported[optionName] = { value: value, ts: now };

    if (PLACEHOLDER_RE.test(value)) return; // placeholder re-selected — not a real choice

    var host = select.closest('variant-selects');
    var productInfo = select.closest('product-info');
    var source = 'main_product';
    if (productInfo && productInfo.dataset.updateUrl === 'false') source = 'featured_product';

    var ds = (host && host.dataset) || {};
    var params = {
      option_name: optionName,
      option_value: value,
      previous_value: prev,
      from_placeholder: prev != null && PLACEHOLDER_RE.test(prev),
      selection_method: selectionMethod(optionName),
      source: source,
      product_id: ds.productId || (productInfo && productInfo.dataset.productId) || null,
      product_title: ds.productTitle || null,
      product_handle: ds.productHandle || null,
      currency: ds.currencyCode || null
    };

    send('variant_option_select', params, {
      standardEvent: 'CustomizeProduct',
      extra: {
        content_ids: params.product_id ? [String(params.product_id)] : [],
        content_type: 'product',
        content_name: params.product_title || ''
      }
    });
  }

  /* ---------------- size-guide modal tracking ---------------- */

  function getModal() {
    return document.getElementById('size-guide-modal');
  }

  function triggerTypeForOpen() {
    var recent = Date.now() - lastClick.ts < 800 ? lastClick.el : null;
    if (recent && recent.closest) {
      if (recent.closest('a.dw-size-guide-link')) return 'variant_picker_link';
      if (recent.closest('[data-open-size-guide]')) return 'button';
      var a = recent.closest('a[href]');
      if (a && (a.getAttribute('href') || '').indexOf('#size-guide') !== -1) return 'link';
      return 'other_click';
    }
    return 'hash_auto_open';
  }

  function watchModalOpen() {
    var modal = getModal();
    if (!modal || typeof MutationObserver !== 'function') return;
    modalWasOpen = modal.classList.contains('is-open');
    var obs = new MutationObserver(function () {
      var open = modal.classList.contains('is-open');
      if (open && !modalWasOpen) {
        send('size_guide_open', { trigger_type: triggerTypeForOpen() });
      }
      modalWasOpen = open;
    });
    obs.observe(modal, { attributes: true, attributeFilter: ['class'] });
  }

  function onDocumentClick(e) {
    if (!e.target || !e.target.closest) return;
    lastClick.el = e.target;
    lastClick.ts = Date.now();

    var tile = e.target.closest('.sg-vcard[data-sg-size]');
    if (!tile) return;

    sizeGuideClickTs = Date.now();

    var modal = getModal();
    var productUrl = (modal && modal.getAttribute('data-sg-product-url')) || '';
    var path = productUrl.split('?')[0].split('#')[0];
    var onProductPage = path && window.location.pathname.indexOf(path) !== -1;
    var hasSizeSelect = !!document.querySelector('variant-selects select');

    send('size_guide_size_select', {
      size_number: tile.getAttribute('data-sg-size'),
      size_value: tile.getAttribute('data-sg-size-value') || null,
      action: onProductPage && hasSizeSelect ? 'in_place' : 'redirect'
    });
  }

  /* ---------------- init ---------------- */

  function init() {
    try {
      var params = new URLSearchParams(window.location.search);
      sgUrlParamAtLoad = params.has('sg_size');
    } catch (e) { /* old browsers */ }

    seedAll();
    // Re-seed lazily: Dawn re-renders the picker on variant change, replacing
    // the <select> elements; focusin catches them before the next user change.
    document.addEventListener('focusin', function (e) {
      if (e.target && e.target.matches && e.target.matches('variant-selects select')) seedSelect(e.target);
    });

    document.addEventListener('change', onDocumentChange);
    document.addEventListener('click', onDocumentClick, true);
    watchModalOpen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
